import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  HORMONE_TRENDS_LEFT_GUTTER_WIDTH,
  HORMONE_TRENDS_RIGHT_GUTTER_WIDTH,
} from '../../../common/utils/services/hormone-trends-utils';
import {
  CyclePhaseBand,
  CyclePhaseBandId,
} from '../../../common/model/type/hormone-trends.type';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

const PHASE_BAND_COLOURS: Record<CyclePhaseBandId, string> = {
  period: Colours.OV_PERIOD_FLOW,
  follicular: Colours.OV_ACCENT_TEAL_TINT,
  ovulation_window: Colours.OV_HIGH_OVULATION,
  luteal: Colours.OV_LIGHT_BLUE,
};

export interface OvCyclePhaseStripProps {
  columnCount: number;
  phaseBands: CyclePhaseBand[];
  leftGutterWidth?: number;
  rightGutterWidth?: number;
}

const OvCyclePhaseStrip: FC<OvCyclePhaseStripProps> = ({
  columnCount,
  phaseBands,
  leftGutterWidth = HORMONE_TRENDS_LEFT_GUTTER_WIDTH,
  rightGutterWidth = HORMONE_TRENDS_RIGHT_GUTTER_WIDTH,
}) => {
  const { t } = useTranslation();

  if (columnCount === 0 || phaseBands.length === 0) {
    return null;
  }

  return (
    <Track
      data-testid="cycle-phase-strip"
      data-column-count={columnCount}
      $columnCount={columnCount}
      $leftGutterWidth={leftGutterWidth}
      $rightGutterWidth={rightGutterWidth}
    >
      {phaseBands.map((band) => (
        <Band
          key={band.phase}
          data-testid={`cycle-phase-band-${band.phase}`}
          data-start-index={band.startIndex}
          data-end-index={band.endIndex}
          $colour={PHASE_BAND_COLOURS[band.phase]}
          style={{
            gridColumn: `${band.startIndex + 1} / ${band.endIndex + 2}`,
          }}
        >
          {t(band.labelKey)}
        </Band>
      ))}
    </Track>
  );
};

export default OvCyclePhaseStrip;

const Track = styled.div<{
  $columnCount: number;
  $leftGutterWidth: number;
  $rightGutterWidth: number;
}>`
  display: grid;
  grid-template-columns: repeat(${({ $columnCount }) => $columnCount}, 1fr);
  padding: 0 ${({ $rightGutterWidth }) => $rightGutterWidth}px 0
    ${({ $leftGutterWidth }) => $leftGutterWidth}px;
`;

const Band = styled.div<{ $colour: string }>`
  min-width: 0;
  padding: 0.375rem 0.25rem;
  overflow: hidden;
  border-right: 1px solid ${Colours.OV_WHITE};
  background-color: ${({ $colour }) => $colour};
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.SMALL};
  line-height: ${Variables.lineHeights.LARGE};
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
